import React, { useEffect, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const caseStudies = [{
  slug: 'hextronics',
  company: 'Hextronics',
  industry: 'Manufacturing',
  title: 'How Hextronics replaced manual production tracking with real-time visibility',
  description: 'Hextronics needed a production system that matched their fast-moving drone manufacturing operation. After legacy systems failed them, Thruline built an iterative solution that grew with their needs.',
  image: 'https://images.unsplash.com/photo-1473968512647-3e447244af8f?auto=format&fit=crop&q=80&w=800&h=400',
  results: ['Single source of truth', 'Full inventory tracking', 'Iterative system build']
}, {
  slug: 'xbow-systems',
  company: 'XBow Systems',
  industry: 'Defense Manufacturing',
  title: 'Building a GovCloud-ready ERP for defense manufacturing',
  description: 'XBow needed an ERP that met strict government compliance requirements while keeping their engineering team productive across three sites. Thruline built it from their existing workflows.',
  image: 'https://images.unsplash.com/photo-1517976487492-5750f3195933?auto=format&fit=crop&q=80&w=800&h=400',
  results: ['GovCloud compliant', 'Full traceability', 'Multi-site production']
}, {
  slug: 'fredsense',
  company: 'Fredsense',
  industry: 'Biotech',
  title: 'Building a custom lab testing and production suite for biotech R&D',
  description: 'Fredsense needed a completely custom lab testing, tracking, and manufacturing suite. Thruline built a white-labeled ERP that handles everything from contracts to sample tests in 60 days.',
  image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&q=80&w=800&h=400',
  results: ['Faster invoice tracking', 'Unified customer reports', 'Custom white-labeled ERP']
}];
export function CaseStudiesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-navy text-white pt-32 pb-20" data-id="element-992" data-mp="23v94mhmjkj1g">
      <div className="max-w-5xl mx-auto px-6" data-id="element-993" data-mp="23smuqbc6hrg2">
        {/* Header */}
        <motion.div className="text-center mb-20" initial="hidden" animate="visible" variants={fadeUp} transition={{
        duration: 0.5
      }} data-id="element-994" data-mp="1ztnkqcblb4hr">

          <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-995" data-mp="174qdd48x2s3s">
            Case Studies
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6" data-id="element-996" data-mp="1kxg0jc06m32h">
            Real results from real manufacturers
          </h1>
          <p className="text-lg text-white/55 max-w-2xl mx-auto" data-id="element-997" data-mp="sesloebs3k9u">
            See how manufacturing and operations teams use Thruline to replace
            legacy systems and build software that actually works for them.
          </p>
        </motion.div>

        {/* Case Studies List */}
        <motion.div className="space-y-8" variants={stagger} initial="hidden" animate="visible" data-id="element-998" data-mp="j1ablazb7a7i">

          {caseStudies.map(study => <motion.div key={study.company} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-999" data-mp="4cxg0f44vtdj">

              <Link to={`/resources/case-studies/${study.slug}`} className="group block rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-violet-electric/20 hover:bg-white/[0.04] transition-all duration-300" data-id="element-1000" data-mp="9bgyg5jiqn15">

                <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1001" data-mp="n2m4bpdhcbjx">
                  <div className="md:col-span-2 h-56 md:h-auto overflow-hidden" data-id="element-1002" data-mp="n23vzudjm6vn">
                    <img src={study.image} alt={study.company} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1003" data-mp="rzvpgstu611y" />

                  </div>
                  <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center" data-id="element-1004" data-mp="3csqr2efp4cn7">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1005" data-mp="3sz2gxmkd73zx">
                      <span className="text-violet-electric text-xs font-semibold tracking-widest uppercase" data-id="element-1006" data-mp="3o83s1ylb8xl2">
                        {study.industry}
                      </span>
                      <span className="text-white/20" data-id="element-1007" data-mp="3j6wjhxbcfudc">•</span>
                      <span className="text-white/40 text-xs" data-id="element-1008" data-mp="3eb8x1po8xfbj">
                        {study.company}
                      </span>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-violet-electric transition-colors" data-id="element-1009" data-mp="atgz7unfi15f">
                      {study.title}
                    </h2>
                    <p className="text-white/50 text-base leading-relaxed mb-6" data-id="element-1010" data-mp="v1imlm12aa6">
                      {study.description}
                    </p>
                    <div className="flex flex-wrap gap-3 mb-4" data-id="element-1011" data-mp="2ql4t1h75bdk0">
                      {study.results.map(result => <span key={result} className="px-3 py-1.5 rounded-full bg-violet-electric/10 text-violet-electric text-xs font-medium" data-id="element-1012" data-mp="2lk7hhngn5hrh">

                          {result}
                        </span>)}
                    </div>
                    <span className="inline-flex items-center gap-2 text-violet-electric text-sm font-medium group-hover:gap-3 transition-all" data-id="element-1013" data-mp="16087s4hj407h">
                      Read case study
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1014" data-mp="1ftf7p9ti2o06" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>)}
        </motion.div>

        {/* CTA */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} transition={{
        duration: 0.5
      }} className="text-center mt-20" data-id="element-1015" data-mp="1ouvihio88s4x">

          <p className="text-white/40 text-base mb-6" data-id="element-1016" data-mp="1yovve6pr31o7">
            Want to see how Thruline can work for your operation?
          </p>
          <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1017" data-mp="4cpbmjisjcka">

            Start Free Discovery
            <ArrowRightIcon className="w-4 h-4" data-id="element-1018" data-mp="6a5r0kwt3eq3" />
          </a>
        </motion.div>
      </div>
    </div>;
}